/**
 * Выбор сообщества после входа.
 *
 * Показывается, когда сервер не выбран: доступных сообществ несколько,
 * либо запомненное перестало быть доступным. Единственный сервер выбирается
 * без участия человека, так что этот экран он не увидит.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { TIER_ROLES_LABEL, type SessionUser } from '@ak/shared';

import { apiGet } from '../lib/api.ts';
import { selectGuild, type GuildAccess } from '../lib/guilds.ts';
import { GuildCard, LogoutButton } from '../components/guild-switcher.tsx';
import { IconLock } from '../components/icons.tsx';
import { Button, Panel, Spinner } from '../components/ui.tsx';

export function GuildPickerPage({ user, inviteUrl }: { user: SessionUser; inviteUrl: string | null }): ReactNode {
  const [guilds, setGuilds] = useState<GuildAccess[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    apiGet<{ guilds: GuildAccess[] }>('/api/guilds', controller.signal)
      .then((response) => {
        setGuilds(response.guilds);
      })
      .catch((cause: unknown) => {
        if (cause instanceof DOMException && cause.name === 'AbortError') {
          return;
        }

        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить список серверов');
      });

    return () => {
      controller.abort();
    };
  }, []);

  const choose = (guildId: string): void => {
    setPending(guildId);
    setError(null);

    selectGuild(guildId).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : 'Не удалось выбрать сервер');
      setPending(null);
    });
  };

  /*
   * Единственный доступный сервер выбирается сам.
   *
   * Раньше это делал сервер — но только когда бот вообще управлял одним
   * сообществом. Игроку выбирать нечего: тир-роль у него на одном сервере,
   * и экран со списком из одной строки — лишний шаг между кнопкой в Discord
   * и цифрами.
   */
  useEffect(() => {
    const only = guilds?.length === 1 ? guilds[0] : undefined;
    if (only === undefined) {
      return;
    }

    setPending(only.guildId);
    selectGuild(only.guildId).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : 'Не удалось выбрать сервер');
      setPending(null);
    });
  }, [guilds]);

  return (
    <div className="grid min-h-screen place-items-center p-4">
      <Panel className="animate-in w-full max-w-lg p-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Выберите сообщество</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--text-muted)' }}>
              Вы вошли как {user.displayName}. Права считаются отдельно для каждого сервера.
            </p>
          </div>
          <LogoutButton />
        </div>

        {error !== null && (
          <p className="mb-4 text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}

        {guilds === null && error === null && (
          <div className="flex items-center justify-center gap-2 py-10" style={{ color: 'var(--text-muted)' }}>
            <Spinner size={18} />
            Загрузка серверов…
          </div>
        )}

        {/*
          Пустой список означает одно из двух: доступной роли нет или
          человека нет на сервере. Разницу знает только Discord, поэтому
          вместо догадки перечисляем роли, которые открывают админку.
        */}
        {guilds !== null && guilds.length === 0 && (
          <div className="py-6 text-center">
            <div
              className="mx-auto grid size-12 place-items-center rounded-2xl"
              style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}
              aria-hidden="true"
            >
              <IconLock size={24} />
            </div>
            <p className="mt-4 font-medium">Доступ закрыт</p>
            <p className="mx-auto mt-2 max-w-sm text-sm" style={{ color: 'var(--text-muted)' }}>
              Статистика каптов открыта игрокам семьи с ролью{' '}
              <span style={{ color: 'var(--text-secondary)' }}>{TIER_ROLES_LABEL}</span>, остальные разделы —
              модерации. Если роль вам недавно выдали, обновите страницу.
            </p>

            {inviteUrl !== null && (
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              <Button
                onClick={() => {
                  window.location.reload();
                }}
              >
                Проверить снова
              </Button>
              <a href={inviteUrl} target="_blank" rel="noreferrer">
                <Button variant="ghost">Добавить бота на свой сервер</Button>
              </a>
            </div>
            )}
          </div>
        )}

        {guilds !== null && guilds.length > 0 && (
          <>
            <div className="flex flex-col gap-2">
              {guilds.map((guild) => (
                <GuildCard
                  key={guild.guildId}
                  guild={guild}
                  busy={pending !== null}
                  onSelect={() => {
                    choose(guild.guildId);
                  }}
                />
              ))}
            </div>

            {inviteUrl !== null && (
            <a
              href={inviteUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-5 block text-center text-sm"
              style={{ color: 'var(--accent)' }}
            >
              Добавить бота на другой сервер ↗
            </a>
            )}
          </>
        )}
      </Panel>
    </div>
  );
}
